import { DateTimeRange } from '@modules/common/components/DateRangePicker/types';
import { EqlFieldName } from '@modules/common/constants';
import { Category } from '@modules/common/types';
import {
  getFieldNameFromEqlField,
  getMetadataQBFieldFromEqlField,
} from '@modules/common/utils';
import { QuantifyState } from '@modules/quantify/store/types';
import {
  getDateTimeRangeFromCompareQuery,
  getHasMetadataFilterForEqlField,
} from '@modules/quantify/utils';
import { getWhereClauseFromCompareQuery } from '@modules/quantify/utils/getWhereClauseFromCompareQuery';
import { TimeUnit } from 'generated/graphql';
import { TimeRange } from 'v2/core/entities';
import { QueryContainer } from 'v2/core/services/notebook/types';
import { combineWhereClauses } from 'v2/core/services/queryBuilder/combineWhereClauses';
import { decodeSourceFromString } from 'v2/core/services/queryBuilder/utils';
import {
  OperationType,
  WhereClause,
} from 'v2/core/services/queryBuilder/whereClause';
import {
  categoryWhereClauseGenerator,
  getWhereClauseForFieldName,
} from 'v2/core/services/queryBuilder/whereClauseGenerator';

interface GetCompareQueryContainerOptions {
  query: QuantifyState['query'];
  compareQuery: QuantifyState['query']['compareQuery'];
  metricKeyId: string | undefined;
  metricCategory: Category | undefined;
  isFieldA: boolean;
  whereClause: WhereClause;
  duration: DateTimeRange;
}

export const getCompareQueryContainer = ({
  query,
  compareQuery,
  metricKeyId,
  metricCategory,
  isFieldA,
  whereClause,
  duration,
}: GetCompareQueryContainerOptions): QueryContainer | null => {
  if (!query.groupByField || !compareQuery) {
    return null;
  }

  const compareWhereClause = getWhereClauseFromCompareQuery({
    isFieldA,
    whereClause,
    compareQuery,
  });

  const compareDateTimeRange = getDateTimeRangeFromCompareQuery({
    duration,
    compareQuery,
    isFieldA,
  });

  const measuredAsCriteriaWhereClause = getHasMetadataFilterForEqlField(
    query.formulaQuery?.criteria,
  );

  if (!metricKeyId) {
    const groupByWhereClause = getHasMetadataFilterForEqlField(
      query.groupByField,
    );

    const unionOfCompareAndGroupByWhereClause = groupByWhereClause
      ? combineWhereClauses(
          compareWhereClause,
          groupByWhereClause,
          OperationType.AND,
        )
      : compareWhereClause;

    return {
      AggregatePeriod: TimeUnit.Week,
      DateRange: compareDateTimeRange as TimeRange,
      WhereClause: measuredAsCriteriaWhereClause
        ? combineWhereClauses(
            unionOfCompareAndGroupByWhereClause,
            measuredAsCriteriaWhereClause,
            OperationType.AND,
          )
        : unionOfCompareAndGroupByWhereClause,
    };
  }

  const dataPointWhereClause = getWhereClauseForFieldName({
    fieldName: getFieldNameFromEqlField(query.groupByField),
    values:
      query.groupByField.field === EqlFieldName.Source
        ? [decodeSourceFromString(metricKeyId)]
        : [metricKeyId],
    op: OperationType.AND,
    qbField: getMetadataQBFieldFromEqlField(query.groupByField),
  });

  const dataPointClauseWithCategory =
    metricCategory && query.groupByField?.field === EqlFieldName.Entity
      ? combineWhereClauses(
          dataPointWhereClause,
          categoryWhereClauseGenerator([metricCategory]),
          OperationType.AND,
        )
      : dataPointWhereClause;

  const unionOfCompareAndDataPointWhereClause = combineWhereClauses(
    dataPointClauseWithCategory,
    compareWhereClause,
    OperationType.AND,
  );

  return {
    AggregatePeriod: TimeUnit.Week,
    DateRange: compareDateTimeRange as TimeRange,
    WhereClause: measuredAsCriteriaWhereClause
      ? combineWhereClauses(
          unionOfCompareAndDataPointWhereClause,
          measuredAsCriteriaWhereClause,
          OperationType.AND,
        )
      : unionOfCompareAndDataPointWhereClause,
  };
};
